/**
 * 하루, 인력 한 명 계산 (공통/돈계산 1-2 계산 순서, 2026-11-04 새 기준)
 *
 * 1. 기본 금액 = 단가 x 공수(따로 바뀐 사람이면 바꾼 단가)
 * 2. 추가 근무 금액 = 단가 x 추가 공수, 또는 직접 적은 금액
 * 3. 소개요금이 붙는 금액 = 기본 금액(올린 단가를 "그대로 유지하기"면 원래 단가 x 공수까지만) + 추가 근무(붙이기일 때)
 *    단가를 내렸을 때는 묻지 않고 맞춘다(유지하기를 골라도 내린 단가로 계산, 2026-11-04 대표님)
 * 4. 소개요금 합계: 사무소 요율(협회 산식: 임금 = 붙는 금액 / (1 + 구인자 %), 구인자 몫 = 붙는 금액 - 임금, 구직자 몫 = 임금 x 구직자 %, 반올림)
 *    또는 현장이 정한 합계(금액이나 단가의 %). 거래처 몫과 인력 몫 나누기는 서류 기능 때 정한다(2026-11-04).
 * 5. 세금 기준(과세 임금) = 기본 금액 + 추가 근무 금액. 단가 전액이다(2026-11-04 세무사 답, 대표님). 이동비, 식대, 사무소가 더 주는 돈은 넣지 않는다.
 *    세금은 지급 한 건 단위로 정하므로 여기서 떼지 않는다(tax.ts payoutTax).
 * 6. 인력이 받는 돈(세금 빼기 전) = 기본 + 추가 - 소개요금 + 인력이 받아갈 추가 청구 + 사무소가 더 주는 돈 - 오늘 빼기로 한 돈
 * 7. 거래처 청구 = 기본 + 추가 + 추가 청구 전부. 현장 직접 지급이면 그 사람 소개요금 전체만 청구하고 사무소가 보내는 돈은 0(2026-11-04 대표님)
 */
import { big, num, roundHalf, ONE, bp, timesGongsu, pctRound } from './arith.ts';

export type FeeRule =
  | { kind: 'rate'; employer_pct: number; worker_pct: number }
  | { kind: 'total'; amount: number }
  | { kind: 'total_pct'; pct: number };

export interface DayInput {
  /** 현장 단가(원래 단가) */
  unit_price: number;
  /** 따로 바뀐 사람의 바꾼 단가 */
  adjusted_price?: number;
  /** 올린 단가에서 "소개요금 그대로 유지하기"(true) 또는 "변경된 단가에 맞추기"(false, 기본) */
  keep_fee?: boolean;
  /** 공수(소수 둘째 자리까지) */
  gongsu: number;
  /** 추가 근무: 공수나 금액 중 하나, 소개요금 붙이기 */
  extra?: { gongsu?: number; amount?: number; fee_on: boolean } | null;
  fee: FeeRule;
  /** 거래처에 더 청구하는 돈 중 인력이 받아갈 것(이동비 등), 사무소가 받아갈 것 */
  extras_to_worker?: number;
  extras_to_office?: number;
  /** 사무소가 자기 돈으로 더 주는 돈(청구에 안 들어가고 세금을 안 뗌, 세무사 답 2026-11-04) */
  office_extra?: number;
  /** 오늘 빼기로 한 돈(가불, 숙소비, 식비, 보험료 등) */
  deduct_today?: number;
  /** 지급하는 쪽(기본 사무소) */
  pay_by?: '사무소' | '현장';
}

export interface DayResult {
  /** 노무비 = 기본 + 추가 근무 */
  labor_amount: number;
  /** 소개요금이 붙는 금액, 안 붙는 금액 */
  fee_base: number;
  no_fee_amount: number;
  /** 소개요금 합계 */
  fee_total: number;
  /** 세금 기준(과세 임금, 단가 전액) */
  taxable: number;
  /** 인력이 받는 돈(세금 빼기 전) */
  pay_before_tax: number;
  /** 사무소가 오늘 보낼 돈(세금 빼기 전). 현장 직접 지급이면 0 */
  send_amount: number;
  /** 거래처 청구 */
  bill_amount: number;
}

function money(n: number | undefined): bigint {
  const v = big(n ?? 0);
  if (v < 0n) throw new Error('금액은 0원 이상이어야 합니다.');
  return v;
}

/** 소개요금 합계 */
export function feeTotal(fee_base: bigint, rule: FeeRule, unit_price: bigint): bigint {
  if (rule.kind === 'rate') {
    const wage = roundHalf(fee_base * ONE, ONE + bp(rule.employer_pct));
    return fee_base - wage + pctRound(wage, rule.worker_pct);
  }
  if (rule.kind === 'total_pct') return pctRound(fee_base, rule.pct);
  if (unit_price === 0n) return 0n;
  return roundHalf(fee_base * money(rule.amount), unit_price);
}

export function calcDay(input: DayInput): DayResult {
  const unit = money(input.unit_price);
  const adjusted = input.adjusted_price === undefined ? null : money(input.adjusted_price);
  const price = adjusted ?? unit;
  const raised = adjusted !== null && adjusted > unit;
  const base = timesGongsu(price, input.gongsu);

  let feeBase = raised && input.keep_fee === true ? timesGongsu(unit, input.gongsu) : base;
  let noFee = base - feeBase;

  let extra = 0n;
  if (input.extra) {
    extra = input.extra.amount !== undefined ? money(input.extra.amount) : timesGongsu(price, input.extra.gongsu ?? 0);
    if (input.extra.fee_on) feeBase += extra;
    else noFee += extra;
  }

  const fee = feeTotal(feeBase, input.fee, unit);
  const labor = base + extra;
  const toWorker = money(input.extras_to_worker);
  const toOffice = money(input.extras_to_office);
  const pay = labor - fee + toWorker + money(input.office_extra) - money(input.deduct_today);
  const direct = input.pay_by === '현장';

  return {
    labor_amount: num(labor),
    fee_base: num(feeBase),
    no_fee_amount: num(noFee),
    fee_total: num(fee),
    taxable: num(labor),
    pay_before_tax: num(pay),
    send_amount: direct ? 0 : num(pay),
    bill_amount: num(direct ? fee : labor + toWorker + toOffice)
  };
}

/**
 * 법정 상한 막기 (계속 막는 것, 2026-10-31): 소개요금 합계 <= 붙는 금액 x (구인자 % + 구직자 %) / (1 + 구인자 %)
 * 반올림 때문에 1원 미만 넘는 것은 허용한다(상한을 올림한 값까지).
 */
export function capOk(fee_total: number, fee_base: number, std: { cap_employer_pct: number; cap_worker_pct: number }): boolean {
  const e = bp(std.cap_employer_pct);
  const top = big(fee_base) * (e + bp(std.cap_worker_pct));
  const den = ONE + e;
  return big(fee_total) * den <= top + den - 1n;
}

/** 공수 제안: 일한 시간 / 1공수 시간을 0.5 단위로 반올림(분 단위로 받음) */
export function suggestGongsu(worked_minutes: number, gongsu_minutes: number): number {
  if (gongsu_minutes <= 0) throw new Error('1공수 시간이 없습니다.');
  return Math.floor((4 * worked_minutes + gongsu_minutes) / (2 * gongsu_minutes)) / 2;
}
